import { Fade, Paper, Popper } from '@mui/material';
import classnames from 'classnames';
import type { PopperProps } from '@mui/material';
import type { FunctionComponent } from 'react';

type Props = Pick<PopperProps, 'anchorEl' | 'open' | 'id' | 'placement'> & {
  popperClassName?: string;
  paperClassName?: string;
  onClose: () => void;
};

/**
 * StickyMenu is a menu that doesn't disable scrolling when opened and will move along with its anchor element
 * when the page is scrolled. This is preferable for pages where we don't want the scrollbar to appear/disappear
 * when a menu is opened/closed.
 *
 * @example
 * import React, { useState } from 'react';
 * import { MenuItem } from '@mui/material';
 *
 * import { Button, StickyMenu } from '../generic';
 *
 * const MyComponent = () => {
 *   const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
 *
 *   const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
 *     setAnchorEl(anchorEl ? null : event.currentTarget);
 *   };
 *
 *   return (
 *     <Button onClick={handleClick}>Click me!</Button>
 *     <StickyMenu
 *       anchorEl={anchorEl}
 *       open={Boolean(anchorEl)}
 *       id="my-sticky-menu"
 *     >
 *       <MenuItem>Hello!</MenuItem>
 *       <MenuItem>World!</MenuItem>
 *     </StickyMenu>
 *   )
 * };
 *
 */
const StickyMenu: FunctionComponent<Props> = ({
  anchorEl,
  open,
  popperClassName,
  paperClassName,
  children,
  id,
  placement,
  onClose,
}) => {
  return (
    <Popper
      id={id}
      anchorEl={anchorEl}
      open={open}
      className={classnames('fossa-sticky-menu', popperClassName)}
      transition
      placement={placement ?? 'bottom-start'}
      onMouseLeave={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      {({ TransitionProps }) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <Fade {...TransitionProps} timeout={350}>
          <Paper elevation={4} className={paperClassName}>
            {children}
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

export default StickyMenu;
